import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ArtistPortalPlaylistCurator } from 'fym-shared/src/smartlinks/artistPortals';
import { TypedMemo } from '../../../common/types';
import { usePortalPlaylists } from '../hooks/usePortalPlaylists';
import { sourceLook } from '../streamingSources';
import { streamingCuratorMessages } from '../streamingCuratorMessages';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { PlaylistsTable } from '../parts/PlaylistsTable';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ALL = 'all';
const CURATORS = Object.values(ArtistPortalPlaylistCurator);

interface PlaylistsSectionProps {
  accountId: number;
}

/** Streaming → Playlists (design d3), with the source and curator type filters. */
const PlaylistsSectionComponent: React.FC<PlaylistsSectionProps> = ({ accountId }) => {
  const intl = useIntl();
  const { playlists, isLoading, error, retry } = usePortalPlaylists(accountId);
  const [source, setSource] = useState<string>(ALL);
  const [curator, setCurator] = useState<string>(ALL);

  const sources = useMemo(() => [...new Set(playlists.map((playlist) => playlist.integrationType))], [playlists]);
  const shown = useMemo(
    () =>
      playlists.filter(
        (playlist) =>
          (source === ALL || playlist.integrationType === source) &&
          (curator === ALL || playlist.curatorType === curator)
      ),
    [curator, playlists, source]
  );

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.playlists)}
      meta={intl.formatMessage(messages.playlistsCount, { count: shown.length })}
      isLoading={isLoading}
      error={error}
      retry={retry}
      testId="streaming-section-playlists"
    >
      <div className={styles.controls}>
        <select
          className={styles.select}
          aria-label={intl.formatMessage(messages.colSource)}
          value={source}
          onChange={(event) => setSource(event.target.value)}
        >
          <option value={ALL}>{intl.formatMessage(messages.allStoresFilter)}</option>
          {sources.map((one) => (
            <option key={one} value={one}>
              {sourceLook(one).name}
            </option>
          ))}
        </select>
        <select
          className={styles.select}
          aria-label={intl.formatMessage(messages.colCurator)}
          value={curator}
          onChange={(event) => setCurator(event.target.value)}
        >
          <option value={ALL}>{intl.formatMessage(messages.allCuratorsFilter)}</option>
          {CURATORS.map((one) => (
            <option key={one} value={one}>
              {intl.formatMessage(streamingCuratorMessages[one])}
            </option>
          ))}
        </select>
      </div>
      {shown.length === 0 ? (
        <p className={styles.faint}>{intl.formatMessage(messages.emptyList)}</p>
      ) : (
        <PlaylistsTable playlists={shown} />
      )}
      <p className={styles.footnote}>
        {intl.formatMessage(messages.playlistsNoteStreams)} {intl.formatMessage(messages.playlistsNoteFollowers)}{' '}
        {intl.formatMessage(messages.playlistsNoteSkip)}
      </p>
    </StreamingBlock>
  );
};

export const PlaylistsSection = TypedMemo(PlaylistsSectionComponent);
